import { useMemo, useState } from "react";
import { BarChart3, ShieldCheck, Star } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useMatchPlayerStats } from "@/hooks/useMatchPlayerStats";
import type {
  ProviderFixturePlayersTeam,
  ProviderPlayerMatchStat,
} from "@/lib/sports/providers/types";
import type { MatchLifecycle } from "@/lib/dataPolicy";

interface Props {
  fixtureId: string | undefined;
  lifecycle: MatchLifecycle;
  homeCode?: string;
  awayCode?: string;
}

/**
 * Real Data Phase 4 — Official player statistics module.
 *
 * Flow: API-Football → ApiFootballProvider → UnifiedSports →
 *       useMatchPlayerStats → this UI.
 *
 * Renders ONLY when the payload confirms `official === true` and
 * `provider === "api_football"`. Never fabricates values; missing metrics
 * render as `—`. Pre-match/live-without-data/finished-without-data all
 * fall back to the lifecycle-appropriate placeholder from `dataPolicy.ts`.
 */
export function OfficialPlayerStats({ fixtureId, lifecycle, homeCode, awayCode }: Props) {
  const { t } = useLanguage();
  const { data, isLoading, isError } = useMatchPlayerStats(fixtureId, lifecycle);

  const isOfficial =
    !!data?.official && data?.provider === "api_football" &&
    ((data?.home?.players.length ?? 0) > 0 || (data?.away?.players.length ?? 0) > 0);

  const placeholderLabel = (() => {
    if (isError) return t("player_stats_provider_error");
    if (lifecycle === "SCHEDULED") return t("player_stats_pre_match");
    if (lifecycle === "LIVE") return t("player_stats_waiting_live");
    return t("player_stats_finished_unavailable");
  })();

  return (
    <section
      className="rounded-2xl border border-border/40 glass p-3 sm:p-4"
      aria-label={t("player_stats_title")}
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden />
          <h3 className="text-sm font-black tracking-tight">{t("player_stats_title")}</h3>
        </div>
        {isOfficial && (
          <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-primary">
            <ShieldCheck className="h-3 w-3" aria-hidden />
            {t("lineups_source_api_football")}
          </span>
        )}
      </header>

      {isLoading ? (
        <Placeholder label={t("player_stats_loading")} />
      ) : !isOfficial ? (
        <Placeholder label={placeholderLabel} homeCode={homeCode} awayCode={awayCode} />
      ) : (
        <TwoTeamSelector
          home={data!.home}
          away={data!.away}
          homeCode={homeCode}
          awayCode={awayCode}
        />
      )}
    </section>
  );
}

function Placeholder({
  label, homeCode, awayCode,
}: { label: string; homeCode?: string; awayCode?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border/40 bg-background/40 px-3 py-6 text-center">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      {(homeCode || awayCode) && (
        <p className="text-[10px] text-muted-foreground/70">
          {homeCode ?? "?"} — {awayCode ?? "?"}
        </p>
      )}
    </div>
  );
}

function TwoTeamSelector({
  home, away, homeCode, awayCode,
}: {
  home: ProviderFixturePlayersTeam | null;
  away: ProviderFixturePlayersTeam | null;
  homeCode?: string;
  awayCode?: string;
}) {
  const teams = [home, away].filter(
    (t): t is ProviderFixturePlayersTeam => !!t && t.players.length > 0,
  );
  const [teamIdx, setTeamIdx] = useState(0);
  const active = teams[Math.min(teamIdx, teams.length - 1)];

  if (!active) return null;
  const codes = [homeCode, awayCode];

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        {teams.map((tm, i) => (
          <button
            key={tm.teamId}
            type="button"
            onClick={() => setTeamIdx(i)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-black tracking-wide transition ${
              i === teamIdx
                ? "border-primary/60 bg-primary/15 text-primary"
                : "border-border/40 bg-background/50 text-muted-foreground hover:text-foreground"
            }`}
          >
            {tm.teamLogo ? (
              <img src={tm.teamLogo} alt="" className="h-3.5 w-3.5 rounded-sm object-contain" />
            ) : null}
            <span className="truncate max-w-[10rem]">{tm.teamName}</span>
            <span className="opacity-60">({codes[i] ?? "?"})</span>
          </button>
        ))}
      </div>

      <PlayerTable players={active.players} />
    </div>
  );
}

function PlayerTable({ players }: { players: ProviderPlayerMatchStat[] }) {
  const { t } = useLanguage();
  const sorted = useMemo(() => {
    return [...players].sort((a, b) => {
      // Non-substitutes first, then by minutes/rating desc.
      const sa = a.substitute ? 1 : 0;
      const sb = b.substitute ? 1 : 0;
      if (sa !== sb) return sa - sb;
      const ma = a.minutes ?? -1;
      const mb = b.minutes ?? -1;
      if (mb !== ma) return mb - ma;
      const ra = a.rating ?? -1;
      const rb = b.rating ?? -1;
      return rb - ra;
    });
  }, [players]);

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="min-w-full text-[11px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            <Th className="pl-2">#</Th>
            <Th>{t("player_stats_col_player")}</Th>
            <Th className="text-center">{t("player_stats_col_pos")}</Th>
            <Th className="text-center">{t("player_stats_col_min")}</Th>
            <Th className="text-center">{t("player_stats_col_rating")}</Th>
            <Th className="text-center">{t("player_stats_col_g")}</Th>
            <Th className="text-center">{t("player_stats_col_a")}</Th>
            <Th className="text-center">{t("player_stats_col_sh")}</Th>
            <Th className="text-center">{t("player_stats_col_pass")}</Th>
            <Th className="text-center">{t("player_stats_col_tkl")}</Th>
            <Th className="text-center">{t("player_stats_col_cards")}</Th>
            <Th className="text-center pr-2">{t("player_stats_col_sv")}</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => (
            <PlayerRow key={p.playerId || `${p.name}-${p.shirtNumber ?? "?"}`} p={p} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`py-1.5 text-[10px] font-black uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`py-1.5 tabular-nums ${className}`}>{children}</td>;
}

/** Renders a value as `—` when null/undefined; never zero-fills. */
function fmt(v: number | null | undefined, suffix = ""): string {
  if (v === null || v === undefined) return "—";
  return `${v}${suffix}`;
}

function ratingTone(r: number | null): string {
  if (r === null) return "text-muted-foreground";
  if (r >= 8) return "text-emerald-400";
  if (r >= 7) return "text-lime-400";
  if (r >= 6) return "text-amber-300";
  return "text-rose-400";
}

function PlayerRow({ p }: { p: ProviderPlayerMatchStat }) {
  const passesLabel =
    p.passes === null && p.passAccuracy === null
      ? "—"
      : `${fmt(p.passes)}${p.passAccuracy !== null ? ` (${p.passAccuracy}%)` : ""}`;
  const cardsLabel =
    p.yellowCards === null && p.redCards === null
      ? "—"
      : `${p.yellowCards ?? 0}Y / ${p.redCards ?? 0}R`;

  return (
    <tr className="border-t border-border/20 hover:bg-white/[0.02]">
      <Td className="pl-2 text-muted-foreground">{fmt(p.shirtNumber)}</Td>
      <Td>
        <div className="flex items-center gap-2">
          {p.photo ? (
            <img
              src={p.photo}
              alt=""
              loading="lazy"
              className="h-6 w-6 rounded-full object-cover ring-1 ring-border/40"
            />
          ) : (
            <div className="h-6 w-6 rounded-full bg-muted/40" />
          )}
          <div className="flex min-w-0 items-center gap-1">
            <span className="truncate font-semibold text-foreground">{p.name}</span>
            {p.captain ? (
              <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-primary/20 text-[9px] font-black text-primary">
                C
              </span>
            ) : null}
            {p.substitute ? (
              <span className="ml-1 rounded bg-muted/40 px-1 text-[9px] uppercase text-muted-foreground">
                sub
              </span>
            ) : null}
          </div>
        </div>
      </Td>
      <Td className="text-center text-muted-foreground">{p.position ?? "—"}</Td>
      <Td className="text-center">{fmt(p.minutes, "'")}</Td>
      <Td className={`text-center font-black ${ratingTone(p.rating)}`}>
        <span className="inline-flex items-center gap-0.5">
          {p.rating !== null ? <Star className="h-3 w-3" aria-hidden /> : null}
          {p.rating !== null ? p.rating.toFixed(1) : "—"}
        </span>
      </Td>
      <Td className="text-center">{fmt(p.goals)}</Td>
      <Td className="text-center">{fmt(p.assists)}</Td>
      <Td className="text-center">
        {p.shots === null && p.shotsOnTarget === null
          ? "—"
          : `${p.shots ?? 0}/${p.shotsOnTarget ?? 0}`}
      </Td>
      <Td className="text-center">{passesLabel}</Td>
      <Td className="text-center">{fmt(p.tackles)}</Td>
      <Td className="text-center">{cardsLabel}</Td>
      <Td className="pr-2 text-center">{fmt(p.saves)}</Td>
    </tr>
  );
}
